// Kronk Test Chatbot - React Compatible JavaScript
import { marked } from 'marked';

export class KronkChatbot {
    constructor(containerElement) {
        this.sessionId = null;
        this.messageCount = 0;
        this.apiBaseUrl = process.env.REACT_APP_CHAT_API_BASE || 'https://api.helios-core.com'; // FastAPI endpoint
        this.isTyping = false;
        this.containerElement = containerElement;
        
        this.initializeElements();
        this.bindEvents();
        this.generateSessionId();
        this.checkApiStatus();
        this.setWelcomeTimestamp();
        
        // Ensure session ID is set after a short delay if initial attempt failed
        setTimeout(() => {
            if (!this.elements.sessionId?.textContent || this.elements.sessionId.textContent === 'Loading...') {
                console.log('🔍 DEBUG: Retrying session ID generation...');
                this.generateSessionId();
            }
        }, 100);
    }

    initializeElements() {
        this.elements = {
            sessionId: this.containerElement?.querySelector('#sessionId'),
            newSessionBtn: this.containerElement?.querySelector('#newSessionBtn'),
            endSessionBtn: this.containerElement?.querySelector('#endSessionBtn'),
            chatMessages: this.containerElement?.querySelector('#chatMessages'),
            chatContainer: this.containerElement?.querySelector('.chatbot-container'),
            messageInput: this.containerElement?.querySelector('#messageInput'),
            sendButton: this.containerElement?.querySelector('#sendButton'),
            typingIndicator: this.containerElement?.querySelector('#typingIndicator'),
            apiStatus: this.containerElement?.querySelector('#apiStatus'),
            messageCount: this.containerElement?.querySelector('#messageCount')
        };
        
        // Debug: Check which elements were found
        console.log('🔍 DEBUG: Elements initialization:');
        Object.entries(this.elements).forEach(([key, element]) => {
            console.log(`🔍 DEBUG: ${key}:`, element ? 'Found' : 'NOT FOUND');
        });
    }

    bindEvents() {
        console.log('🔍 DEBUG: Binding events...');
        
        if (this.elements.sendButton) {
            console.log('🔍 DEBUG: Send button found, adding click listener');
            this.elements.sendButton.addEventListener('click', () => this.sendMessage());
        } else {
            console.error('🔍 DEBUG: Send button not found!');
        }
        
        if (this.elements.messageInput) {
            console.log('🔍 DEBUG: Message input found, adding keypress listener');
            this.elements.messageInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') this.sendMessage();
            });
        } else {
            console.error('🔍 DEBUG: Message input not found!');
        }
        
        if (this.elements.newSessionBtn) {
            console.log('🔍 DEBUG: New session button found, adding click listener');
            this.elements.newSessionBtn.addEventListener('click', () => this.newSession());
        } else {
            console.error('🔍 DEBUG: New session button not found!');
        }
        
        if (this.elements.endSessionBtn) {
            console.log('🔍 DEBUG: End session button found, adding click listener');
            this.elements.endSessionBtn.addEventListener('click', () => this.endSession());
        } else {
            console.error('🔍 DEBUG: End session button not found!');
        }
        
        // Event delegation for product expand buttons and variant selection
        if (this.elements.chatContainer) {
            this.elements.chatContainer.addEventListener('click', (e) => {
                if (e.target.classList.contains('product-expand-btn')) {
                    const productId = e.target.getAttribute('data-product-id');
                    if (productId) {
                        this.toggleProductExpansion(productId);
                    }
                }
            });
            
            // Event delegation for variant selection changes
            this.elements.chatContainer.addEventListener('change', (e) => {
                if (e.target.classList.contains('variant-select')) {
                    const productId = e.target.getAttribute('data-product-id');
                    if (productId) {
                        console.log('🔍 DEBUG: Variant selection changed via event delegation for product:', productId);
                        this.updateAddToCartButton(productId);
                    }
                }
            });
        } else {
            console.error('🔍 DEBUG: chatContainer not found, event delegation disabled');
        }
    }

    generateSessionId() {
        this.sessionId = `test_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        console.log('🔍 DEBUG: Generated session ID:', this.sessionId);
        
        if (this.elements.sessionId) {
            this.elements.sessionId.textContent = this.sessionId;
            console.log('🔍 DEBUG: Session ID displayed successfully');
        } else {
            console.error('🔍 DEBUG: Session ID element not found!');
        }
    }

    newSession() {
        this.generateSessionId();
        this.clearChat();
        this.addMessage('assistant', 'New session started! How can I help you with boxing equipment today?');
        this.messageCount = 1;
        this.updateMessageCount();
    }

    clearChat() {
        if (this.elements.chatMessages) {
            this.elements.chatMessages.innerHTML = '';
        }
    }

    setWelcomeTimestamp() {
        const timestamp = this.containerElement?.querySelector('#welcomeTimestamp');
        if (timestamp) {
            timestamp.textContent = this.formatTimestamp(new Date());
        }
    }

    formatTimestamp(date) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    async sendMessage(message = null) {
        console.log('🔍 DEBUG: sendMessage called with:', message);
        const messageText = message || this.elements.messageInput?.value?.trim();
        console.log('🔍 DEBUG: messageText:', messageText);
        console.log('🔍 DEBUG: isTyping:', this.isTyping);
        
        if (!messageText || this.isTyping) {
            console.log('🔍 DEBUG: Message not sent - empty text or already typing');
            return;
        }

        // Add user message (only if it's from input, not synthetic)
        if (!message) {
            console.log('🔍 DEBUG: Adding user message to chat');
            this.addMessage('user', messageText);
            if (this.elements.messageInput) {
                this.elements.messageInput.value = '';
            }
        }
        // For synthetic messages, don't show them in chat - they're just for backend
        
        // Show typing indicator
        console.log('🔍 DEBUG: Showing typing indicator');
        this.showTyping(true);
        this.disableInput(true);

        try {
            // Call Kronk API
            const response = await this.callKronkAPI(messageText);
            
            // Hide typing indicator
            this.showTyping(false);
            this.disableInput(false);
            
            // Add assistant response
            this.addMessage('assistant', response);
            
            // Focus input for next message
            if (this.elements.messageInput) {
                this.elements.messageInput.focus();
            }
            
        } catch (error) {
            console.error('Error calling Kronk API:', error);
            this.showTyping(false);
            this.disableInput(false);
            
            // Provide a helpful error message with instructions
            const errorMessage = `I'm Kronk, your boxing equipment expert! I can help you find:
- Boxing gloves and hand wraps
- Training equipment and bags
- Apparel and accessories
- Size recommendations
- Product comparisons

What are you looking for today?

*Note: Running in demo mode. Connect the real API for full functionality.*`;
            
            this.addMessage('assistant', errorMessage);
            this.updateApiStatus('error');
        }
    }

    async callKronkAPI(message) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/kronk/chat`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-KEY': `${process.env.REACT_APP_API_KEY}`
                },
                body: JSON.stringify({
                    message: message,
                    session_id: this.sessionId,
                    history: []  // Let the server retrieve from blob storage
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            this.updateApiStatus('connected');
            
            // Clean up the response - remove extra quotes if present
            let cleanResponse = data.response;
            if (typeof cleanResponse === 'string' && cleanResponse.startsWith('"') && cleanResponse.endsWith('"')) {
                cleanResponse = cleanResponse.slice(1, -1);
            }
            
            return cleanResponse;
        } catch (error) {
            console.error('❌ API call failed:', error);
            this.updateApiStatus('error');
            
            // Provide mock responses for testing when API is not available
            return this.getMockResponse(message);
        }
    }

    getMockResponse(message) {
        const lowerMessage = message.toLowerCase();
        
        // Mock responses based on common questions
        if (lowerMessage.includes('what do you do') || lowerMessage.includes('what can you help')) {
            return `I'm Kronk, your boxing equipment expert! I can help you with:

🥊 **Boxing Equipment:**
- Boxing gloves and hand wraps
- Training equipment and heavy bags
- Apparel and accessories
- Size recommendations

🛍️ **Shopping Assistance:**
- Product comparisons
- Price information
- Availability checks
- Order support

💬 **Just ask me:**
- "Show me boxing gloves under $100"
- "What size should I get?"
- "Tell me about your return policy"

*Note: This is a demo response. Connect the real API for full functionality.*`;
        }
        
        if (lowerMessage.includes('gloves') || lowerMessage.includes('boxing gloves')) {
            return `Great choice! Here are some popular boxing gloves:

🥊 **Training Gloves:**
- **Beginner**: 14-16oz for protection
- **Intermediate**: 12-14oz for speed
- **Advanced**: 10-12oz for competition

💡 **Size Guide:**
- **Small hands**: 10-12oz
- **Medium hands**: 12-14oz  
- **Large hands**: 14-16oz

*Note: This is a demo response. Connect the real API for actual product listings.*`;
        }
        
        if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('$')) {
            return `Our boxing equipment ranges from:

💰 **Price Ranges:**
- **Basic gloves**: $30-60
- **Premium gloves**: $60-150
- **Training bags**: $50-200
- **Apparel**: $20-80

💳 **We offer:**
- Free shipping on orders over $75
- 30-day return policy
- Size exchanges

*Note: This is a demo response. Connect the real API for current pricing.*`;
        }
        
        // Default response
        return `Thanks for your message: "${message}"

I'm Kronk, your boxing equipment assistant! I can help you find the perfect gear for your training needs.

**Try asking:**
- "What boxing gloves do you recommend?"
- "Show me training equipment"
- "What's your return policy?"

*Note: This is a demo response. Connect the real API for full functionality.*`;
    }

    getChatHistory() {
        const messages = [];
        const messageElements = this.elements.chatMessages?.querySelectorAll('.message') || [];
        
        messageElements.forEach(msg => {
            const isUser = msg.classList.contains('user');
            const content = msg.querySelector('.message-content p')?.textContent;
            msg.classList.add(isUser ? 'user' : 'assistant');
            if (content) {
                messages.push({
                    role: isUser ? 'user' : 'assistant',
                    content: content
                });
            }
        });
        
        return messages;
    }

    addMessage(role, content) {
        console.log('🔍 DEBUG: addMessage called with role:', role, 'content length:', content?.length);
        
        if (!this.elements.chatMessages) {
            console.error('🔍 DEBUG: chatMessages element not found!');
            return;
        }

        const messageDiv = document.createElement('div');
        messageDiv.className = `message ${role}`;
        
        // Clean up content - remove extra quotes and escape characters
        let cleanContent = content;
        if (typeof cleanContent === 'string') {
            try {
                // Try to parse as JSON first
                const jsonContent = JSON.parse(cleanContent);
                cleanContent = jsonContent;
            } catch (e) {
                // If not JSON, clean up string content
                if (cleanContent.startsWith('"') && cleanContent.endsWith('"')) {
                    cleanContent = cleanContent.slice(1, -1);
                }
                cleanContent = cleanContent.replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\t/g, '\t');
            }
        }
        
        console.log('🔍 DEBUG: addMessage called with content type:', typeof cleanContent);
        console.log('🔍 DEBUG: Content preview:', typeof cleanContent === 'string' ? cleanContent.substring(0, 200) : 'JSON object', '...');
        
        // Handle different content types
        let contentHtml = '';
        try {
            // First check if it's a product response
            if (typeof cleanContent === 'object' && cleanContent.response_type === 'product_display') {
                console.log('🔍 DEBUG: Detected as product display response');
                contentHtml = this.renderStructuredProductResponse(cleanContent);
            } else if (this.isProductListing(cleanContent)) {
                console.log('🔍 DEBUG: Detected as product listing');
                contentHtml = this.renderProductListing(cleanContent);
            } else if (this.isJsonResponse(cleanContent)) {
                console.log('🔍 DEBUG: Detected as JSON response');
                contentHtml = this.renderJsonResponse(cleanContent);
            } else {
                console.log('🔍 DEBUG: Detected as regular text');
                // Render markdown for regular text content
                contentHtml = `<div class="markdown-content">${this.renderMarkdown(cleanContent)}</div>`;
            }
        } catch (error) {
            console.error('🔍 DEBUG: Error in addMessage content processing:', error);
            contentHtml = `<div class="markdown-content">${this.renderMarkdown(typeof cleanContent === 'object' ? JSON.stringify(cleanContent, null, 2) : cleanContent)}</div>`;
        }
        
        messageDiv.innerHTML = `
            <div class="message-content">
                ${contentHtml}
            </div>
            <div class="message-timestamp">${this.formatTimestamp(new Date())}</div>
        `;
        
        this.elements.chatMessages.appendChild(messageDiv);
        this.elements.chatMessages.scrollTop = this.elements.chatMessages.scrollHeight;
        
        this.messageCount++;
        this.updateMessageCount();
        
        console.log('🔍 DEBUG: Message added successfully, total count:', this.messageCount);
    }

    renderMarkdown(text) {
        try {
            // Configure marked.js options for safe rendering
            marked.setOptions({
                breaks: true,        // Convert line breaks to <br>
                gfm: true,          // GitHub Flavored Markdown
                sanitize: false,    // Allow HTML (we'll sanitize manually)
                smartLists: true,   // Better list handling
                smartypants: true   // Smart typography
            });
            
            // Render markdown to HTML
            const html = marked.parse(text);
            
            // Basic sanitization for security
            return this.sanitizeHtml(html);
        } catch (error) {
            console.error('Error rendering markdown:', error);
            // Fallback to plain text if markdown rendering fails
            return `<p>${this.escapeHtml(text)}</p>`;
        }
    }

    sanitizeHtml(html) {
        // Create a temporary div to parse and sanitize HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Allow safe HTML tags and attributes
        const allowedTags = {
            'p': ['class'],
            'h1': ['class'], 'h2': ['class'], 'h3': ['class'], 'h4': ['class'], 'h5': ['class'], 'h6': ['class'],
            'strong': [], 'b': [],
            'em': [], 'i': [],
            'u': [],
            'code': ['class'],
            'pre': ['class'],
            'blockquote': ['class'],
            'ul': ['class'], 'ol': ['class'],
            'li': ['class'],
            'a': ['href', 'target', 'class'],
            'br': [],
            'hr': ['class'],
            'span': ['class'],
            'div': ['class']
        };
        
        // Recursively sanitize nodes
        this.sanitizeNode(tempDiv, allowedTags);
        
        return tempDiv.innerHTML;
    }

    sanitizeNode(node, allowedTags) {
        if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase();
            
            if (allowedTags.hasOwnProperty(tagName)) {
                // Keep allowed tags, remove disallowed attributes
                const allowedAttrs = allowedTags[tagName];
                const attrs = Array.from(node.attributes);
                
                attrs.forEach(attr => {
                    if (!allowedAttrs.includes(attr.name)) {
                        node.removeAttribute(attr.name);
                    }
                });
                
                // Special handling for links
                if (tagName === 'a' && node.hasAttribute('href')) {
                    const href = node.getAttribute('href');
                    if (!href.startsWith('http://') && !href.startsWith('https://') && !href.startsWith('/')) {
                        node.removeAttribute('href');
                        node.setAttribute('href', '#');
                    }
                }
                
                // Recursively sanitize child nodes
                Array.from(node.childNodes).forEach(child => {
                    this.sanitizeNode(child, allowedTags);
                });
            } else {
                // Remove disallowed tags, but keep their content
                const parent = node.parentNode;
                if (parent) {
                    while (node.firstChild) {
                        parent.insertBefore(node.firstChild, node);
                    }
                    parent.removeChild(node);
                }
            }
        } else if (node.nodeType === Node.TEXT_NODE) {
            // Text nodes are safe, no action needed
        }
    }

    isProductListing(content) {
        // Check if content is a structured JSON response
        console.log('🔍 DEBUG: isProductListing called with content type:', typeof content);
        console.log('🔍 DEBUG: isProductListing content preview:', typeof content === 'string' ? content.substring(0, 100) + '...' : content);
        
        try {
            // If content is already an object, use it directly
            const parsed = typeof content === 'object' ? content : JSON.parse(content);
            const isProduct = parsed && 
                   parsed.response_type === 'product_display' && 
                   parsed.product_array && 
                   Object.keys(parsed.product_array).length > 0;
            console.log('🔍 DEBUG: isProductListing result:', isProduct);
            if (isProduct) {
                console.log('🔍 DEBUG: Product array keys:', Object.keys(parsed.product_array));
            }
            return isProduct;
        } catch (e) {
            console.log('🔍 DEBUG: isProductListing JSON parse error:', e.message);
            console.log('🔍 DEBUG: Full content that failed to parse:', content);
            // Not JSON, so not a product response
            return false;
        }
    }

    isJsonResponse(content) {
        if (typeof content === 'object') return true;
        try {
            JSON.parse(content);
            return true;
        } catch {
            return false;
        }
    }

    renderJsonResponse(content) {
        try {
            // If content is already an object, use it directly
            const parsed = typeof content === 'object' ? content : JSON.parse(content);
            // If it's a product response, use the product renderer
            if (parsed.response_type === 'product_display') {
                return this.renderStructuredProductResponse(parsed);
            }
            // For other JSON responses, just display as formatted JSON
            return `<div class="markdown-content"><pre><code>${JSON.stringify(parsed, null, 2)}</code></pre></div>`;
        } catch (e) {
            console.error('Error parsing JSON response:', e);
            return `<div class="markdown-content">${this.renderMarkdown(content)}</div>`;
        }
    }

    renderProductListing(content) {
        try {
            // If content is already an object, use it directly
            const parsed = typeof content === 'object' ? content : JSON.parse(content);
            
            if (parsed.response_type === 'product_display') {
                return this.renderStructuredProductResponse(parsed);
            }
            
            // Fallback for other JSON responses
            return `<div class="markdown-content">${this.renderMarkdown(typeof content === 'object' ? JSON.stringify(content, null, 2) : content)}</div>`;
            
        } catch (e) {
            console.error('Error parsing product response:', e);
            return `<div class="markdown-content">${this.renderMarkdown(typeof content === 'object' ? JSON.stringify(content, null, 2) : content)}</div>`;
        }
    }

    renderStructuredProductResponse(parsed) {
        let html = '';
        
        // Add starting message
        if (parsed.starting_message) {
            html += `<div class="message-intro">${this.renderMarkdown(parsed.starting_message)}</div>`;
        }
        
        // Add product cards
        if (parsed.product_array && Object.keys(parsed.product_array).length > 0) {
            html += this.renderProductCards(parsed.product_array);
        }
        
        // Add ending message
        if (parsed.ending_message) {
            html += `<div class="message-outro">${this.renderMarkdown(parsed.ending_message)}</div>`;
        }
        
        return html;
    }

    renderProductCards(products) {
        let html = `
            <div class="product-carousel">
                <div class="product-scroll-container">
        `;
        
        Object.entries(products).forEach(([productId, product], index) => {
            const uniqueId = `product-${Date.now()}-${index}`;
            
            html += `
                <div class="product-card-horizontal ${product.available ? '' : 'product-unavailable'}" 
                     data-product-id="${productId}"
                     data-product-name="${this.escapeHtml(product.title || product.productName || product.name)}">
                    
                    <!-- Product Image -->
                    <div class="product-image-container">
                        <img src="${product.images?.[0]?.src || product.image || ''}" 
                             alt="${this.escapeHtml(product.title || product.productName || product.name)}" 
                             class="product-image" />
                    </div>
            
                    <!-- Product Info -->
                    <div class="product-info">
                        <div class="product-name">${this.escapeHtml(product.title || product.productName || product.name)}</div>
                        <div class="product-price">$${product.price_range?.min || product.price || '0.00'}</div>
                        
                        <!-- Availability Status -->
                        <div class="product-availability ${product.available ? 'available' : 'unavailable'}">
                            ${product.available ? '✅ In Stock' : '❌ Out of Stock'}
                        </div>
                        
                        <div class="product-description">
                            ${this.escapeHtml(product.short_description || product.description || 'No description available')}
                        </div>
                        
                        <!-- Variants Selection -->
                        ${this.renderVariantSelector(product, productId, uniqueId)}
                        
                        <!-- Action Buttons -->
                        <div class="product-actions">
                        <button class="product-expand-btn" data-product-id="${uniqueId}">
                            View Details
                        </button>
                            ${product.available ? `
                                <button class="add-to-cart-btn disabled" 
                                        id="addToCart-${productId}"
                                        onclick="window.handleAddToCartWithVariants('${productId}', '${uniqueId}')"
                                        disabled>
                                    Select Variant
                                </button>
                            ` : ''}
                    </div>
                    </div>
                    
                    <!-- Expanded Details -->
                    <div class="product-expanded-details" id="${uniqueId}" style="display: none;">
                        <div class="product-details">
                            <h5>Product Details:</h5>
                            <div class="detail-item">
                                <strong>Description:</strong>
                                <p>${this.escapeHtml(product.short_description || product.description || 'No description available')}</p>
                            </div>
                            
                            ${product.product_url ? `
                                <div class="detail-item">
                                    <strong>Product Page:</strong>
                                    <a href="${product.product_url}" target="_blank" class="product-link">
                                        View on KRONK Website →
                                    </a>
                                </div>
                            ` : ''}
                            
                            ${product.tags && product.tags.length > 0 ? `
                                <div class="detail-item">
                                    <strong>Tags:</strong>
                                    <div class="product-tags">
                                        ${product.tags.map(tag => `<span class="tag">${this.escapeHtml(tag)}</span>`).join('')}
                                    </div>
                                </div>
                            ` : ''}
                        </div>
                    </div>
                </div>
            `;
        });
        
        html += `
                </div>
            </div>
        `;
        
        // Add debugging after DOM is updated
        setTimeout(() => {
            console.log('🔍 DEBUG: Product cards rendered, checking for variant selects...');
            const variantSelects = this.containerElement?.querySelectorAll('.variant-select') || [];
            console.log('🔍 DEBUG: Found', variantSelects.length, 'variant selects in DOM');
            
            variantSelects.forEach((select, index) => {
                console.log(`🔍 DEBUG: Variant select ${index}:`, {
                    productId: select.getAttribute('data-product-id'),
                    value: select.value,
                    options: select.options.length
                });
            });
        }, 500);
        
        return html;
    }

    renderVariantSelector(product, productId, uniqueId) {
        console.log('🔍 DEBUG: renderVariantSelector called for product:', productId);
        console.log('🔍 DEBUG: Product variants:', product.variants);
        
        if (!product.variants || product.variants.length === 0) {
            console.log('🔍 DEBUG: No variants found, enabling button immediately');
            // For products without variants, enable the add to cart button immediately
            setTimeout(() => {
                const addToCartBtn = this.containerElement?.querySelector(`#addToCart-${productId}`);
                if (addToCartBtn) {
                    addToCartBtn.disabled = false;
                    addToCartBtn.classList.remove('disabled');
                    addToCartBtn.textContent = 'Add to Cart';
                }
            }, 100);
            return '<div class="variant-selector"><p class="no-variants">No variations available</p></div>';
        }
        
        // Group variants by type (size, color, etc.)
        const variantGroups = this.groupVariants(product.variants);
        console.log('🔍 DEBUG: Variant groups:', variantGroups);
        
        let html = '<div class="variant-selector">';
        
        Object.entries(variantGroups).forEach(([groupName, variants]) => {
            const availableVariants = variants.filter(v => v.available);
            console.log(`🔍 DEBUG: Group "${groupName}" has ${availableVariants.length} available variants`);
            if (availableVariants.length === 0) return;
            
            html += `
                <div class="variant-group">
                    <label class="variant-label">${groupName}:</label>
                    <select class="variant-select" data-group="${groupName}" data-product-id="${productId}">
                        <option value="">Select ${groupName}</option>
                        ${availableVariants.map(variant => `
                            <option value="${variant.variant_id}" data-variant='${JSON.stringify(variant)}'>
                                ${this.getVariantDisplayName(variant.title)} - $${variant.price}
                            </option>
                        `).join('')}
                    </select>
                </div>
            `;
        });
        
        html += '</div>';
        console.log('🔍 DEBUG: Generated variant selector HTML for product:', productId);
        return html;
    }

    groupVariants(variants) {
        const groups = {};
        
        variants.forEach(variant => {
            // Extract size from title (e.g., "Jet Black / XS" -> "Size")
            const parts = variant.title.split(' / ');
            if (parts.length > 1) {
                const size = parts[1];
                if (!groups['Size']) groups['Size'] = [];
                groups['Size'].push({ ...variant, size });
            } else {
                if (!groups['Options']) groups['Options'] = [];
                groups['Options'].push(variant);
            }
        });
        
        return groups;
    }

    getVariantDisplayName(variantTitle) {
        // Extract the size/option part from variant title
        const parts = variantTitle.split(' / ');
        return parts.length > 1 ? parts[1] : variantTitle;
    }

    toggleProductExpansion(uniqueId) {
        const expandedDetails = this.containerElement?.querySelector(`#${uniqueId}`);
        
        if (!expandedDetails) {
            console.error('Could not find expanded details element with ID:', uniqueId);
            return;
        }
        
        // Find the button within the same product card
        const productCard = expandedDetails.closest('.product-card-horizontal');
        const expandBtn = productCard?.querySelector('.product-expand-btn');
        
        if (expandedDetails.style.display === 'none' || expandedDetails.style.display === '') {
            expandedDetails.style.display = 'block';
            if (expandBtn) expandBtn.textContent = 'Hide Details';
            // Smooth scroll into view
            expandedDetails.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
            expandedDetails.style.display = 'none';
            if (expandBtn) expandBtn.textContent = 'View Details';
        }
    }

    showTyping(show) {
        this.isTyping = show;
        if (this.elements.typingIndicator) {
            this.elements.typingIndicator.style.display = show ? 'block' : 'none';
        }
    }

    disableInput(disable) {
        if (this.elements.messageInput) {
            this.elements.messageInput.disabled = disable;
        }
        if (this.elements.sendButton) {
            this.elements.sendButton.disabled = disable;
        }
    }

    updateMessageCount() {
        if (this.elements.messageCount) {
            this.elements.messageCount.textContent = this.messageCount;
        }
    }

    updateApiStatus(status) {
        const statusElement = this.elements.apiStatus;
        if (!statusElement) return;

        statusElement.className = `status-${status}`;
        
        switch (status) {
            case 'connecting':
                statusElement.textContent = 'Connecting...';
                break;
            case 'connected':
                statusElement.textContent = 'Connected';
                break;
            case 'error':
                statusElement.textContent = 'Error';
                break;
            default:
                statusElement.textContent = 'Unknown';
                break;
        }
    }

    async endSession() {
        if (!this.sessionId) return;

        try {
            // Show loading state
            this.showTyping(true);
            this.disableInput(true);
            if (this.elements.endSessionBtn) {
                this.elements.endSessionBtn.disabled = true;
            }

            // Call end session endpoint with session_id as query parameter
            const response = await fetch(`${this.apiBaseUrl}/kronk/end_session?session_id=${encodeURIComponent(this.sessionId)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-KEY': `${process.env.REACT_APP_API_KEY}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            await response.json();
            
            // Add success message
            this.addMessage('assistant', 'Session ended successfully. Starting a new session...');
            
            // Start a new session
            setTimeout(() => this.newSession(), 1000);

        } catch (error) {
            console.error('Error ending session:', error);
            this.addMessage('assistant', 'Error ending session. Please try again.');
        } finally {
            // Reset UI state
            this.showTyping(false);
            this.disableInput(false);
            if (this.elements.endSessionBtn) {
                this.elements.endSessionBtn.disabled = false;
            }
        }
    }

    async checkApiStatus() {
        try {
            const response = await fetch(`${this.apiBaseUrl}/health`, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-KEY': `${process.env.REACT_APP_API_KEY}`
                }
            });
            if (response.ok) {
                this.updateApiStatus('connected');
                console.log('✅ API connected successfully');
            } else {
                this.updateApiStatus('error');
                console.log('❌ API returned error status:', response.status);
            }
        } catch (error) {
            this.updateApiStatus('error');
            console.log('❌ API connection failed:', error.message);
            console.log('💡 Make sure the FastAPI backend is running on http://localhost:8000');
        }
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    updateAddToCartButton(productId) {
        console.log('🔍 DEBUG: updateAddToCartButton called for product:', productId);
        
        const productCard = this.containerElement?.querySelector(`[data-product-id="${productId}"]`);
        if (!productCard) {
            console.log('🔍 DEBUG: Product card not found for:', productId);
            return;
        }

        const addToCartBtn = this.containerElement?.querySelector(`#addToCart-${productId}`);
        if (!addToCartBtn) {
            console.log('🔍 DEBUG: Add to cart button not found for:', productId);
            return;
        }

        const variantSelects = productCard.querySelectorAll('.variant-select');
        console.log('🔍 DEBUG: Found', variantSelects.length, 'variant selects for product:', productId);
        
        let hasSelection = false;

        // Check if any variant is selected
        variantSelects.forEach((select, index) => {
            console.log(`🔍 DEBUG: Variant select ${index} value:`, select.value);
            console.log(`🔍 DEBUG: Variant select ${index} selectedIndex:`, select.selectedIndex);
            console.log(`🔍 DEBUG: Variant select ${index} selectedOption:`, select.options[select.selectedIndex]?.text);
            if (select.value && select.value !== '' && select.selectedIndex > 0) {
                hasSelection = true;
            }
        });

        console.log('🔍 DEBUG: Has selection:', hasSelection);

        // Update button state
        if (hasSelection) {
            addToCartBtn.disabled = false;
            addToCartBtn.classList.remove('disabled');
            addToCartBtn.textContent = 'Add to Cart';
            console.log('🔍 DEBUG: Button enabled for product:', productId);
        } else {
            addToCartBtn.disabled = true;
            addToCartBtn.classList.add('disabled');
            addToCartBtn.textContent = 'Select Variant';
            console.log('🔍 DEBUG: Button disabled for product:', productId);
        }
    }

    handleAddToCartWithVariants(productId, uniqueId) {
        // Find the product card
        const productCard = this.containerElement?.querySelector(`[data-product-id="${productId}"]`);
        if (!productCard) {
            console.error('Product card not found:', productId);
            return;
        }

        // Check if button is disabled
        const addToCartBtn = this.containerElement?.querySelector(`#addToCart-${productId}`);
        if (addToCartBtn && addToCartBtn.disabled) {
            // Button is disabled, no need to show error as user can see it's disabled
            return;
        }

        // Get selected variants
        const selectedVariants = [];
        const variantSelects = productCard.querySelectorAll('.variant-select');
        
        // Check if this product has variants
        if (variantSelects.length > 0) {
            // Product has variants - check selections
            variantSelects.forEach(select => {
                const selectedOption = select.options[select.selectedIndex];
                if (selectedOption && selectedOption.value) {
                    try {
                        const variantData = JSON.parse(selectedOption.getAttribute('data-variant'));
                        selectedVariants.push({
                            quantity: 1, // Default quantity
                            merchandise_id: variantData.variant_id
                        });
                    } catch (e) {
                        console.error('Error parsing variant data:', e);
                    }
                }
            });

            // If no variants selected, return silently as button should be disabled
            if (selectedVariants.length === 0) {
                return;
            }
        } else {
            // Product has no variants - this should not happen as button should be disabled
            this.showNotification('No variants available for this product', 'error');
            return;
        }

        // Create cart message
        const cartMessage = {
            lines: selectedVariants
        };

        // Create synthetic message for cart addition
        const syntheticMessage = `Add following items to cart ${JSON.stringify(cartMessage)}`;

        // Show loading state
        this.showCartLoading(true);

        // Send to chat endpoint
        console.log('Sending synthetic message:', syntheticMessage);
        this.sendMessage(syntheticMessage).then(() => {
            this.showCartLoading(false);
            this.showNotification('Item(s) added to cart successfully!', 'success');
        }).catch((error) => {
            this.showCartLoading(false);
            this.showNotification('Failed to add item to cart. Please try again.', 'error');
            console.error('Error adding to cart:', error);
        });
    }

    showCartLoading(show) {
        const addToCartButtons = this.containerElement?.querySelectorAll('.add-to-cart-btn') || [];
        addToCartButtons.forEach(btn => {
            if (show) {
                btn.classList.add('cart-loading');
                btn.disabled = true;
                btn.textContent = 'Adding...';
            } else {
                btn.classList.remove('cart-loading');
                btn.disabled = false;
                btn.textContent = 'Add to Cart';
            }
        });
    }

    showNotification(message, type = 'info') {
        const notification = document.createElement('div');
        notification.className = `notification ${type}`;
        notification.textContent = message;
        
        document.body.appendChild(notification);
        
        // Auto-remove after 3 seconds
        setTimeout(() => {
            notification.remove();
        }, 3000);
    }
}

// Global functions for product interactions
window.toggleProductExpansion = function(uniqueId) {
    // This will be set by the React component
    if (window.currentChatbot) {
        window.currentChatbot.toggleProductExpansion(uniqueId);
    }
};

window.handleAddToCartWithVariants = function(productId, uniqueId) {
    if (window.currentChatbot) {
        window.currentChatbot.handleAddToCartWithVariants(productId, uniqueId);
    }
};

window.updateAddToCartButton = function(productId) {
    if (window.currentChatbot) {
        window.currentChatbot.updateAddToCartButton(productId);
    }
};
